import * as Sentry from '@sentry/react'

// An empty env var should fall back to the default DSN, same as unset.
const dsn =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    import.meta.env.VITE_SENTRY_DSN ||
    'https://71661276bc43a53a85ef7c3719b90123@o4508858653081600.ingest.us.sentry.io/4511181960773632'

const enabled = import.meta.env.MODE !== 'test'

/**
 * Build the Sentry init options.
 *
 * Exported as a factory (fresh integration instances per call) so tests can
 * assert on the real production config rather than a hand-rolled copy of it.
 */
export function createSentryOptions() {
    return {
        dsn,
        environment: import.meta.env.MODE,
        tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1,
        enabled,
        tracePropagationTargets: [/^https:\/\/api\.mackinac\.social/],
    }
}

Sentry.init(createSentryOptions())

// `browserTracingIntegration` must be registered explicitly — the browser
// SDK's `getDefaultIntegrations()` returns a fixed list that does not include
// it and never inspects `tracesSampleRate`, so setting a sample rate alone
// produces no pageload/navigation spans, no fetch instrumentation, and
// therefore no `sentry-trace` request header on outgoing API calls.
//
// It's registered via a dynamic import (rather than passed to `init` above)
// so it, and its `web-vitals` dependency, load in a separate chunk instead of
// bloating the main entry bundle. `addIntegration` attaches it to the client
// created above once the import resolves, moments after initial load.
//
// Gated on `enabled`: unlike `init`'s own integration setup, `addIntegration`
// doesn't check the client's `enabled` flag, so this would otherwise install
// fetch instrumentation even when Sentry is disabled (e.g. in tests, where it
// would wrap `fetch` before test setup gets a chance to stub it).
if (enabled) {
    void import('./sentryTracing').then(({ registerBrowserTracing }) => registerBrowserTracing())
}
